import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, ElementRef, HostListener, inject, signal, Signal, ViewChild, WritableSignal, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CalendarIntegrationStore, FeatureStore, FocusStore, HabitStore, JournalStore, MoneyTrackerStore, TodoStore, ViewSettingsStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { ParseMinutesPipe } from '@app/_pipes/parse-minutes.pipe';
import { CacheService } from '@app/_services/cache.service';
import { BRAND_LOGO } from '@app/_utils/brand-icons';
import { ParseTimePipe } from '@app/_pipes/parse-time.pipe';
import { InputDropdownComponent } from '@app/components/shared/inputs/input-dropdown/input-dropdown.component';
import { InputGoalComponent } from '@app/components/shared/inputs/input-goal/input-goal.component';
import { InputToggleComponent } from '@app/components/shared/inputs/input-toggle/input-toggle.component';
import { JournalBlockComponent } from '../../journals/journal-block/journal-block.component';
import { TodoBlockComponent } from '../../todos/todo-block/todo-block.component';
import { EntityCalendarComponent } from '../entity-calendar/entity-calendar.component';
import { RelativeDatePipe } from '@app/_pipes/relative-date.pipe';
import { InputCalendarComponent, InputCalendarModel } from '@app/components/shared/inputs/input-calendar/input-calendar.component';
import { MoneyTrackerBlockComponent } from '../../money-tracker/money-tracker-block/money-tracker-block.component';
import { CalendarEventBlockComponent } from '../../calendar-integration/calendar-event-block/calendar-event-block.component';
import { HabitBlockComponent } from '../../habits/habit-block/habit-block.component';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { EntityEmptyComponent } from '../entity-empty/entity-empty.component';
import { ShowType } from '@app/_types/generic.type';
import { HabitTimerDialogComponent } from '../../habits/habit-timer-dialog/habit-timer-dialog.component';
import { UnscheduledTodosComponent } from '../../todos/unscheduled-todos/unscheduled-todos.component';
import { HabitTimerService } from '@app/_services/habit-timer.service';
import { SettingsService } from '@app/_services/settings.service';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { AllFeaturesDisabledBlockComponent } from '@app/components/shared/all-features-disabled-block/all-features-disabled-block.component';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { FeatureService } from '@app/_services/features/features.service';
import { MobileBottomBarComponent } from '@app/components/shared/mobile-bottom-bar/mobile-bottom-bar.component';
import { MobileWeeklyCalendarComponent, WeekDay } from '@app/components/shared/mobile-weekly-calendar/mobile-weekly-calendar.component';
import { Entry } from '@app/_models/entry.model';
import { FocusBlockComponent } from '../../focus-tracker/focus-block/focus-block.component';
import { NoteBlockComponent } from '../../notes/note-block/note-block.component';
import { addDays, format as dateFnsFormat, isSameDay, isSunday, startOfWeek, type Day } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { DATE_FNS_LOCALE_MAP } from '@app/_services/date/date-fns-adapter';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatCalendar, MatDatepicker, MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { TodayViewComponent } from '../today-view/today-view.component';
import { EntityCalendarHeader, InputEntityCalendarComponent } from '@app/components/shared/inputs/input-entity-calendar/input-entity-calendar.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CustomDateAdapter, MY_DATE_FORMATS } from '@app/_configs/mat-date.config';
import { MatToolbarModule } from '@angular/material/toolbar';
import { log } from '@app/_services/logger';
import { settings } from '@angular/fire/analytics';
import { ModalService } from '@app/_services/modal.service';
import { TranslationKey } from '@app/_generated/translation-keys';
import { EntityName } from '@app/_types/generic.type';

/**
 * Unscheduled entity group categories, in display order.
 * Matches Flutter's _unscheduledGroupOrder.
 */
const UNSCHEDULED_GROUP_ORDER: EntityName[] = [
  'moneyTracker',
  'focus',
  'todo',
  'habit',
  'journal',
  'note',
];

/**
 * Maps entity names to translation keys for unscheduled group headers.
 * Uses 'unscheduled_titleTopBar' for todos (matching Flutter which says "Unscheduled To-dos").
 */
const UNSCHEDULED_GROUP_NAME_KEYS: Record<string, TranslationKey> = {
  moneyTracker: 'common_moneyTracker',
  focus: 'common_focusTracker',
  todo: 'unscheduled_titleTopBar',
  habit: 'common_habits',
  journal: 'common_journals',
  note: 'common_notes',
};

/**
 * Represents an unscheduled entity group for the chronological view.
 * - 'auto-expanded': no header, items render directly (like normal list items)
 * - 'collapsed': header tile only (feature name + count badge), tappable to expand
 * - 'user-opened': header tile (different bg) + items below, tappable to collapse
 */
export interface UnscheduledDisplayGroup {
  category: EntityName;
  name: string;
  entities: Entry[];
  count: number;
  state: 'auto-expanded' | 'collapsed' | 'user-opened';
}

@Component({
  selector: 'app-entity',
  imports: [
    CommonModule,
    SvgComponent,
    JournalBlockComponent,
    TodoBlockComponent,
    EntityCalendarComponent,
    MoneyTrackerBlockComponent,
    CalendarEventBlockComponent,
    HabitBlockComponent,
    NotificationComponent,
    NoResultBlockComponent,
    MobileBottomBarComponent,
    MobileWeeklyCalendarComponent,
    FocusBlockComponent,
    NoteBlockComponent,
    AllFeaturesDisabledBlockComponent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    CommonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ],
  templateUrl: './entity.component.html',
  styleUrl: './entity.component.scss'
})
export class EntityComponent implements AfterViewInit, OnInit, OnDestroy {

  readonly brandLogo = BRAND_LOGO;
  readonly featureStore = inject(FeatureStore);
  readonly todoStore = inject(TodoStore);
  readonly moneyStore = inject(MoneyTrackerStore);
  readonly habitStore = inject(HabitStore);
  readonly journalStore = inject(JournalStore);
  readonly calendarStore = inject(CalendarIntegrationStore);
  readonly focusStore = inject(FocusStore);
  readonly viewSettings = inject(ViewSettingsStore);

  // ✅ CHANGED: weekDays as WritableSignal instead of plain array
  weekDays: WritableSignal<any[]> = signal([]);

  isCalendarOpen: WritableSignal<boolean> = signal(false);
  dateString: WritableSignal<string> = signal(getDateString());
  todayDateString: string = getDateString();
  unSubscribe = new Subject<void>();
  currentDate: Date = new Date();
  todayDate: Date = new Date();
  mode: 'PRESENT' | 'PAST' | 'FUTURE' = 'PRESENT';
  @ViewChild('calendarWrapper') calendarWrapper!: ElementRef;

  hiddenValues: Signal<ShowType[]> = computed(() => {
    const hiddenValues: ShowType[] = ['doneDate', 'date', 'emptyDays', 'setup'];
    const activeEntities = this.featureStore.activeEntities();
    const isGrouped = this.viewSettings.entityGroupBy() !== 'chronological';
    if (isGrouped) {
      hiddenValues.push('label');
    }
    if (!activeEntities.includes('habit')) {
      hiddenValues.push('habitResponse');
      hiddenValues.push('invalidEntry');
    }
    if (!activeEntities.includes('todo')) {
      hiddenValues.push('checklist');
    }
    if (!activeEntities.includes('calendarIntegration')) {
      hiddenValues.push('calendarName');
    }
    if (!activeEntities.includes('journal')) {
      hiddenValues.push('mood');
      hiddenValues.push('invalidEntry');
    }
    return hiddenValues;
  });

  notApplicableValues: Signal<{ value: string, reason: string }[]> = computed(() => {
    const notApplicableValues = [];
    const isGrouped = this.viewSettings.entityGroupBy() !== 'chronological';
    if (isGrouped) {
      notApplicableValues.push({ value: 'label', reason: this.ss.t('viewConfiguration_notApplicableContent') });
    }
    return notApplicableValues;
  });

  isToday: Signal<boolean> = computed(() => {
    return this.dateString() === this.todayDateString;
  });

  // Track dialog state changes by checking dialog count
  private dialogChangeSignal: WritableSignal<number> = signal(0);

  timerServiceActive: Signal<any[]> = computed(() => {
    const activeTimers = this.timerService.addedTimers().filter(timer => {
      return (
        this.habitStore.idToSetup()[timer.setupId] &&
        this.habitStore.idToSetup()[timer.setupId].deletedAt === null
      );
    });

    return activeTimers;
  });

  isHabitFormOpen: Signal<boolean> = computed(() => {
    // Trigger reactivity by reading dialogChangeSignal
    this.dialogChangeSignal();

    const runningTimeValue = this.timerServiceActive()?.[0];

    const timersCount = this.timerServiceActive()?.length;

    // If multiple timers are running, always show the clock (return false)
    if (timersCount >= 2) {
      return false;
    }

    // Check if habit action screen (HabitFormComponent) is open
    const isHabitActionOpen = this.dialog.openDialogs.some(dialogRef =>
      `${dialogRef.componentInstance?.data?.setupId + '&&&' + (this.isclockedToOpenHabitTimersDialog() ? runningTimeValue.dateString : this.dateString())}` === runningTimeValue.id
    );

    // If one timer running AND habit action screen is open → hide clock
    if (timersCount === 1 && isHabitActionOpen) {
      return true;
    }

    // Show clock by default
    return false;
  });


  @ViewChild(InputEntityCalendarComponent) calendarComp!: InputEntityCalendarComponent;

  screenEnterTime: Signal<Date> = signal(new Date());
  screen: WritableSignal<string> = signal('today');
  groupByFeatures: Signal<{ id: string; name: string; data: Entry[] }[]> = computed(() => {
    const groupByFeature = this.featureStore.groupByFeature();
    return groupByFeature
  });

  // Cache entries to prevent multiple calls and flickering
  entries = computed(() => this.featureStore.entries());

  /**
   * Tracks which collapsed groups the user has manually expanded.
   * This is local UI state, not persisted. Reset when navigating dates.
   */
  userExpandedGroups: WritableSignal<Set<string>> = signal(new Set());

  /**
   * Computes the unscheduled display groups with expand/collapse state
   * for the chronological view. Matches Flutter's _buildDisplayItems algorithm.
   *
   * Rules:
   * - Always expanded when: only 1 unscheduled entity TYPE, OR group has <= 1 entry
   * - Collapse when: total visible entries > 6 AND group has > 1 entry
   * - Greedy: process groups sorted by count ASC, expand smallest first
   *
   * Display states:
   * - auto-expanded: no header, items show directly (like normal list items)
   * - collapsed: header tile only (feature name + count badge)
   * - user-opened: header tile (color6 bg) + items below
   */
  /**
   * All-day calendar events from withoutTime that should render as a flat list.
   * In Flutter, calendar events are always marked as scheduled (isUnscheduled: false),
   * so they appear in the scheduled section. In Angular, all-day calendar events
   * end up in withoutTime. We render them between scheduled and unscheduled groups
   * to maintain parity with Flutter behavior.
   */
  allDayCalendarEvents: Signal<Entry[]> = computed(() => {
    return this.entries().withoutTime.filter(e => e.entityType === 'calendarIntegration');
  });

  /**
   * Scheduled entities that have no startTime (e.g., daily habits with no specific time).
   * These are "scheduled but without time" — they belong in the scheduled section (always expanded),
   * not in the unscheduled (collapsible) section. Matches Flutter's classification by isScheduled flag.
   */
  scheduledWithoutTime: Signal<Entry[]> = computed(() => {
    return this.entries().withoutTime.filter(e =>
        e.isScheduled && e.entityType !== 'calendarIntegration'
    );
  });

  unscheduledDisplayGroups: Signal<UnscheduledDisplayGroup[]> = computed(() => {
    const withoutTime = this.entries().withoutTime;
    // Only include truly unscheduled entities (isUnscheduled === true) in the collapsible groups.
    // Scheduled-without-time entities are rendered in the scheduled section via scheduledWithoutTime.
    const unscheduledEntities = withoutTime.filter(e => !e.isScheduled && e.entityType !== 'calendarIntegration');
    // Include all-day calendar events and scheduled-without-time in scheduled count for threshold calculation
    const allDayCalCount = this.allDayCalendarEvents().length;
    const scheduledWithoutTimeCount = this.scheduledWithoutTime().length;
    const scheduledCount = this.entries().withTime.length + allDayCalCount + scheduledWithoutTimeCount;
    const userExpanded = this.userExpandedGroups();

    // Group unscheduled entities by entityName
    const groupMap = new Map<EntityName, Entry[]>();
    for (const entity of unscheduledEntities) {
      const list = groupMap.get(entity.entityType) ?? [];
      list.push(entity);
      groupMap.set(entity.entityType, list);
    }

    // Build groups with counts in display order, only non-empty groups
    const groupsWithCounts: { category: EntityName; count: number }[] = [];
    for (const category of UNSCHEDULED_GROUP_ORDER) {
      const entities = groupMap.get(category);
      if (entities && entities.length > 0) {
        groupsWithCounts.push({ category, count: entities.length });
      }
    }

    // Collapse decision (updated 2026-03-26 per QA/PO feedback):
    // - "Unscheduled" = total count across ALL unscheduled groups
    // - Collapse ALL groups when: totalEntries > 6 AND totalUnscheduled > 1
    // - Expand ALL groups when: totalEntries <= 6 OR totalUnscheduled <= 1
    const totalUnscheduled = groupsWithCounts.reduce((sum, g) => sum + g.count, 0);
    const totalEntries = scheduledCount + totalUnscheduled;
    const shouldCollapse = totalEntries > 6 && totalUnscheduled > 1;

    const autoExpandedGroups = new Set<EntityName>();
    const collapsedGroups = new Set<EntityName>();

    for (const { category, count } of groupsWithCounts) {
      if (shouldCollapse && count > 1) {
        collapsedGroups.add(category);
      } else {
        autoExpandedGroups.add(category);
      }
    }

    // Build final display groups in the defined display order
    const displayGroups: UnscheduledDisplayGroup[] = [];
    for (const category of UNSCHEDULED_GROUP_ORDER) {
      const entities = groupMap.get(category);
      if (!entities || entities.length === 0) continue;

      const nameKey = UNSCHEDULED_GROUP_NAME_KEYS[category];
      const name = this.ss.t(nameKey) || category;
      const isAutoExpanded = autoExpandedGroups.has(category);
      const isCollapsed = collapsedGroups.has(category);
      const isUserOpened = userExpanded.has(category);

      let state: 'auto-expanded' | 'collapsed' | 'user-opened';
      if (isAutoExpanded) {
        state = 'auto-expanded';
      } else if (isCollapsed && isUserOpened) {
        state = 'user-opened';
      } else {
        state = 'collapsed';
      }

      displayGroups.push({
        category,
        name,
        entities,
        count: entities.length,
        state,
      });
    }

    return displayGroups;
  });

  /**
   * Toggle the expand/collapse state of an unscheduled group.
   */
  toggleGroup(category: EntityName): void {
    const current = new Set(this.userExpandedGroups());
    if (current.has(category)) {
      current.delete(category);
    } else {
      current.add(category);
    }
    this.userExpandedGroups.set(current);
  }

  /**
   * Calculate the index where the timeline should appear (Flutter-style)
   * Returns the index of the first item whose time is AFTER the current time
   * If all items are before current time, returns items.length (timeline at end)
   * If current time is before all items, returns 0 (timeline at top)
   */
  timelineIndex: Signal<number> = computed(() => {
    const currentTime = this.currentTime();
    const entitiesWithTime = this.entries().withTime;

    if (entitiesWithTime.length === 0) {
      return 0;
    }

    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

    for (let i = 0; i < entitiesWithTime.length; i++) {
      const entry = entitiesWithTime[i];

      if (!entry.sortTime) {
        return i; // No time set, show timeline before this item
      }

      const entityMinutes = entry.sortTime.getHours() * 60 + entry.sortTime.getMinutes();

      if (entityMinutes > currentMinutes) {
        return i; // This item is after current time, show timeline before it
      }
    }

    return entitiesWithTime.length; // All items are before current time, show at end
  });

  slideDirection: string = '';
  bodySlideDirection: WritableSignal<'slide-in-left' | 'slide-in-right' | ''> = signal('');
  previousDateString: string = '';

  periodLabel: string = '';
  isMonthView = true;
  private childCalendarDate: Date = new Date();
  private changeDetectorRef = inject(ChangeDetectorRef);
  private closestTimeTickerStarted = false;
  closestTime: WritableSignal<string> = signal('00:00');
  currentTime: WritableSignal<Date> = signal(new Date());
  private isclockedToOpenHabitTimersDialog: WritableSignal<boolean> = signal(false);

  private dateAdapter = inject(DateAdapter);

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    public cc: CacheService,
    public vs: ViewSettingService,
    public timerService: HabitTimerService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    public featureService: FeatureService,
    private modalService: ModalService

  ) {

    // Track dialog state changes to update clock visibility
    this.dialog.afterOpened.pipe(takeUntil(this.unSubscribe)).subscribe((dialogRef) => {
      this.dialogChangeSignal.update(v => v + 1);

      // Also update when this specific dialog closes
      dialogRef.afterClosed().pipe(takeUntil(this.unSubscribe)).subscribe(() => {
        this.dialogChangeSignal.update(v => v + 1);
      });
    });

    // Effect to update week day data flags when store data changes
    // This ensures dots appear correctly after initial data load
    effect(() => {
      // Read entities signal to establish dependency
      const entities = this.entries();

      // Only update if weekDays exist (after init)
      if (this.weekDays().length > 0 && this.currentDate) {
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenChecked error
        setTimeout(() => {
          this.markWeekDataFlags(this.currentDate);
        }, 0);
      }
    });
  }

  ngAfterViewInit() {
    EntityCalendarHeader.arrowClick
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((data: Date) => {
        log.debug('ngAfterViewInit', 'Arrow clicked', { date: data });
        this.childCalendarDate = data;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
          this.addDotsToCalendar(data);
          this.changeDetectorRef.markForCheck();
        }, 50);
      });

    EntityCalendarHeader.yearSelected
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((data: Date) => {
        log.debug('ngAfterViewInit', 'Year selected', { date: data });
        this.childCalendarDate = data;
        this.changeDetectorRef.detectChanges();
        this.addDotsToCalendar(this.childCalendarDate);
        this.changeDetectorRef.markForCheck();
      });

    // Mark week data flags after initial render
    requestAnimationFrame(() => {
      this.markWeekDataFlags(this.currentDate);
      this.changeDetectorRef.markForCheck();
    });

    EntityCalendarHeader.viewChanged
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((data) => {
        this.isMonthView = data === 'month';
        log.debug('ngAfterViewInit', 'View changed', { view: data });
        this.changeDetectorRef.markForCheck();
      });


  }

  onDatePicked(date: Date) {
    const dateString = getDateString(date);
    this.generateWeekDays(date);
    this.markWeekDataFlags(date);
    this.isCalendarOpen.set(false);

    // Navigate and let initWithAnimation handle the animation
    this.router.navigate(dateString === this.todayDateString ? ['/today'] : ['/', dateString]);
  };

  isLastOccurrence(time: string, index: number): boolean {
    const list = this.entries().withTime;
    // Find the last index of items with this time
    let lastIndex = -1;
    for (let i = list.length - 1; i >= 0; i--) {
      const entry = list[i];
      if (!entry.sortTime) continue;
      const h = entry.sortTime.getHours().toString().padStart(2, '0');
      const m = entry.sortTime.getMinutes().toString().padStart(2, '0');
      if (`${h}:${m}` === time) {
        lastIndex = i;
        break;
      }
    }
    return lastIndex === index;
  }

  ngOnInit() {
    // ✅ Initialize previousDateString to enable animations from first navigation
    this.previousDateString = this.dateString();

    this.route.paramMap.subscribe(params => {
      const date = params.get('date');
      document.body.classList.remove('blur');
      if (date && this.isValidDateString(date)) {
        // Redirect to /today if the date matches today's date
        // This ensures the "Today" island menu is always selected when viewing today
        if (date === this.todayDateString) {
          this.router.navigate(['/today'], { replaceUrl: true });
          return;
        }
        this.initWithAnimation(date);
      } else {
        const dateString = getDateString();
        this.initWithAnimation(dateString);
      }
      this.generateWeekDays(this.currentDate);
    });
    this.analyticsInit();
    this.changeDetectorRef.markForCheck();
    this.initClosestTimeTicker();
    setTimeout(() => {
      this.generateWeekDays(this.childCalendarDate);
    }, 5000);
  };

  initClosestTimeTicker() {
    if (this.closestTimeTickerStarted) return; // prevent multiple intervals
    this.closestTimeTickerStarted = true;

    // Emit immediately
    this.closestTime.set(this.featureStore.findClosestTime());
    this.currentTime.set(new Date());

    // Run every 3 seconds
    setInterval(() => {
      this.closestTime.set(this.featureStore.findClosestTime());
      this.currentTime.set(new Date());
    }, 3000);
  }

  /**
   * ✅ Initialize with animation based on date direction
   */
  private initWithAnimation(date: string) {
    const shouldAnimate = this.previousDateString && this.previousDateString !== date;

    if (shouldAnimate) {
      const newDate = new Date(date);
      const prevDate = new Date(this.previousDateString);

      if (newDate > prevDate) {
        this.bodySlideDirection.set('slide-in-left');
      } else if (newDate < prevDate) {
        this.bodySlideDirection.set('slide-in-right');
      }
    }

    this.previousDateString = date;
    this.init(date);

    if (shouldAnimate) {
      setTimeout(() => {
        this.bodySlideDirection.set('');
      }, 350);
    } else {
      // No animation on first load or same date
      this.bodySlideDirection.set('');
    }
  }


  selectDay(day: any) {
    const currentWeekDays = this.weekDays();
    currentWeekDays.forEach(d => d.active = false);
    day.active = true;

    // ✅ Update signal to trigger change detection
    this.weekDays.set([...currentWeekDays]);

    const selectedDateString = getDateString(day.dateObject);

    this.markWeekDataFlags(day.dateObject);

    // ✅ Update childCalendarDate to prevent week view reset after 5s timeout
    this.childCalendarDate = day.dateObject;

    // Navigate and let initWithAnimation handle the animation
    this.router.navigate(
      selectedDateString === this.todayDateString ? ['/today'] : ['/', selectedDateString]
    );
  }

  isValidDateString(dateStr: string): boolean {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateStr)) return false;

    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return false;

    const [year, month, day] = dateStr.split('-').map(Number);
    // Use local date methods (not UTC) since dateString is in local timezone
    return (
      dateObj.getFullYear() === year &&
      dateObj.getMonth() + 1 === month &&
      dateObj.getDate() === day
    );
  }

  init(date: string) {
    this.dateString.set(date);
    this.currentDate = new Date(date);
    this.featureStore.filterDate(date);

    // Reset expanded groups when navigating to a new date
    this.userExpandedGroups.set(new Set());

    // ✅ Update childCalendarDate to prevent week view reset after 5s timeout
    // This ensures that when the route changes, the childCalendarDate stays in sync
    this.childCalendarDate = this.currentDate;

    if (this.currentDate > this.todayDate) {
      this.mode = 'FUTURE';
    } else if (this.currentDate < this.todayDate) {
      this.mode = 'PAST';
    } else {
      this.mode = 'PRESENT';
    }
  }

  readonly entityCalendarHeader = EntityCalendarHeader;
  @ViewChild('picker') picker!: MatDatepicker<Date>;

  openCalendar() {
    this.picker.open();
    setTimeout(() => {
      this.addDotsToCalendar(new Date(this.dateString()));
    }, 100);
  }

  closeCalendar() {
    this.picker.close();
  }

  dateSelected(event: any, picker: MatDatepicker<any>): void {
    let selectedDate: Date | null = null;

    // Moment date
    if (event.value && typeof event.value.toDate === 'function') {
      selectedDate = event.value.toDate();
    }
    // Native JS Date
    else if (event.value instanceof Date) {
      selectedDate = event.value;
    }

    if (selectedDate) {
      log.debug('dateSelected', 'Selected date', { selectedDate });
      this.onDatePicked(selectedDate);
    }

  }


  addDotsToCalendar(date: Date) {
    if (!this.isMonthView) {
      log.debug('addDotsToCalendar', 'Skipping - not in month view');
      return;
    }

    const calendarBody = document.querySelector('.mat-calendar-body');
    if (!calendarBody) {
      log.warn('addDotsToCalendar', 'Calendar body not found');
      return;
    }

    const existingPlaceholders = calendarBody.querySelectorAll('.mat-calendar-body-disabled');
    existingPlaceholders.forEach(el => el.remove());

    const allLabels = calendarBody.querySelectorAll('.mat-calendar-body-label');
    if (allLabels.length > 0) {
      allLabels.forEach(label => label.remove());
    }

    const rows = calendarBody.querySelectorAll('tr');
    rows.forEach((row: any) => {
      const labelInRow = row.querySelector('.mat-calendar-body-label');
      if (labelInRow) {
        row.remove();
      }
    });

    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const firstDayWeekday = firstDayOfMonth.getDay(); // 0=Sun, 6=Sat

    // Get the locale-aware first day of week from the DateAdapter
    // e.g., 0=Sunday (en), 1=Monday (most EU), 6=Saturday (ar)
    const firstDayOfWeek = this.dateAdapter.getFirstDayOfWeek();

    log.debug('addDotsToCalendar', 'Month info', { month: month + 1, year, firstDayWeekday, firstDayOfWeek });

    // Calculate how many placeholder cells to show before the 1st of the month.
    // This is the offset of the 1st's weekday from the locale's first day of week.
    const daysToShowStart = (firstDayWeekday - firstDayOfWeek + 7) % 7;

    if (daysToShowStart > 0) {
      const prevMonthLastDate = new Date(year, month, 0).getDate();
      const prevMonthDates: number[] = [];

      for (let i = daysToShowStart - 1; i >= 0; i--) {
        prevMonthDates.push(prevMonthLastDate - i);
      }

      const firstCell: any = calendarBody.querySelector('.mat-calendar-body-cell-container');

      if (firstCell && firstCell.parentElement) {
        prevMonthDates.forEach((dateNum) => {
          const cellDiv = document.createElement('td');
          cellDiv.className = 'mat-calendar-body-cell-container mat-calendar-body-disabled';
          cellDiv.style.paddingBottom = '4.14286%';

          const span = document.createElement('span');
          span.style.display = 'flex';
          span.style.justifyContent = 'center';
          span.style.color = 'var(--color7)';
          span.style.opacity = '0.6';
          span.style.fontWeight = '500';
          span.textContent = dateNum.toString();

          cellDiv.appendChild(span);
          firstCell.parentElement.insertBefore(cellDiv, firstCell);
        });

        log.debug('addDotsToCalendar', 'Added placeholder cells at START', { count: daysToShowStart });
      }
    }

    const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
    const lastDayOfMonth = new Date(year, month, daysInCurrentMonth);
    const lastDayWeekday = lastDayOfMonth.getDay();

    // Calculate how many placeholder cells to show after the last day of the month.
    // The last column index in the locale's week is (firstDayOfWeek + 6) % 7.
    const lastDayOfWeek = (firstDayOfWeek + 6) % 7;
    const daysToShowEnd = lastDayWeekday === lastDayOfWeek ? 0 : ((lastDayOfWeek - lastDayWeekday + 7) % 7);

    if (daysToShowEnd > 0) {
      const nextMonthDates: number[] = [];

      for (let i = 1; i <= daysToShowEnd; i++) {
        nextMonthDates.push(i);
      }

      const allRows = calendarBody.querySelectorAll('tr');
      const lastRow: any = allRows[allRows.length - 1];

      if (lastRow) {
        nextMonthDates.forEach((dateNum) => {
          const cellDiv = document.createElement('td');
          cellDiv.className = 'mat-calendar-body-cell-container mat-calendar-body-disabled';
          cellDiv.style.paddingBottom = '4.14286%';

          const span = document.createElement('span');
          span.style.display = 'flex';
          span.style.justifyContent = 'center';
          span.style.color = 'var(--color7)';
          span.style.opacity = '0.6';
          span.style.fontWeight = '500';
          span.textContent = dateNum.toString();

          cellDiv.appendChild(span);
          lastRow.appendChild(cellDiv);
        });

        log.debug('addDotsToCalendar', 'Added placeholder cells at END', { count: daysToShowEnd });
      }
    }
    this.addDotsToSpecificDates(date);
  }

  getMonthStartAndEnd(date: Date): { firstDate: Date; lastDate: Date } {
    const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { firstDate, lastDate };
  }

  private getStartOfWeek(date: Date): Date {
    return startOfWeek(date, { weekStartsOn: this.ss.effectiveWeekStartDay() as Day });
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private normalizeDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private parseISODate(dateString: string): Date {
    const [datePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private addDotsToSpecificDates(date: Date) {
    if (!date) {
      log.warn('addDotsToSpecificDates', 'Invalid date provided');
      return;
    }

    try {
      const { firstDate, lastDate } = this.getMonthStartAndEnd(date);

      // Generate all date strings in the month range
      const dateStrings: string[] = [];
      const currentDate = new Date(firstDate);
      while (currentDate <= lastDate) {
        const dateString = [
          currentDate.getFullYear(),
          String(currentDate.getMonth() + 1).padStart(2, '0'),
          String(currentDate.getDate()).padStart(2, '0'),
        ].join('-');
        dateStrings.push(dateString);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Use the SAME logic as UI display to determine which dates have data
      const hasDataMap = this.featureService.getHasDataForDates(dateStrings);

      // Get dates that have data
      const datesWithDots = dateStrings
        .filter(dateString => hasDataMap[dateString])
        .map(dateString => this.parseISODate(dateString))
        .map(d => this.normalizeDate(d));

      log.debug('addDotsToSpecificDates', 'Dates with dots', { count: datesWithDots.length });

      this.changeDetectorRef.markForCheck();

      requestAnimationFrame(() => {
        this.applyDotsToCalendarCells(date, datesWithDots);
      });
    } catch (error) {
      log.error('addDotsToSpecificDates', 'Error adding dots', { error });
    }
  }

  private applyDotsToCalendarCells(date: Date, datesWithDots: Date[]): void {
    const cells = document.querySelectorAll(
      '.mat-calendar-body-cell:not(.mat-calendar-body-disabled)'
    );

    if (cells.length === 0) {
      log.warn('applyDotsToCalendarCells', 'No calendar cells found');
      return;
    }

    cells.forEach((cell: HTMLElement | any) => {
      const cellText = cell.textContent?.trim();
      const cellDate = parseInt(cellText || '', 10);

      if (isNaN(cellDate)) return;

      const cellDateObj = new Date(date.getFullYear(), date.getMonth(), cellDate);
      const normalizedCellDate = this.normalizeDate(cellDateObj);

      const match = datesWithDots.some(d => this.isSameDay(d, normalizedCellDate));

      const hasClass = cell.classList.contains('has-dot');
      if (match && !hasClass) {
        cell.classList.add('has-dot');
      } else if (!match && hasClass) {
        cell.classList.remove('has-dot');
      }
    });
  }

  private getEmptyWeekDays(date: Date): any[] {
    const startOfWeekDate = this.getStartOfWeek(date);
    const langCode = this.ss.appLanguageCode() || 'en';
    const dateFnsLocale = DATE_FNS_LOCALE_MAP[langCode] || enUS;

    // Define a map from JS weekday numbers to your language keys
    const dayKeyMap: TranslationKey[] = [
      'common_daySunday',
      'common_dayMonday',
      'common_dayTuesday',
      'common_dayWednesday',
      'common_dayThursday',
      'common_dayFriday',
      'common_daySaturday'
    ];

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeekDate);
      d.setDate(startOfWeekDate.getDate() + i);
      const dayKey = dayKeyMap[d.getDay()];
      const fullName = this.ss.t(dayKey) || dayKey;
      const narrowName = dateFnsFormat(d, 'EEEEE', { locale: dateFnsLocale });

      return {
        name: fullName,
        narrowName,
        date: d.getDate(),
        dateObject: d,
        active: false,
        isToday: this.isSameDay(d, new Date()),
        hasData: false,
      };
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.calendarWrapper?.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isCalendarOpen.set(false);
    }
  }

  slideAndNavigate(offset: number) {
    this.slideDirection = offset > 0 ? 'slide-left' : 'slide-right';

    setTimeout(() => {
      this.navigateWithOffset(offset);
    }, 50);

    setTimeout(() => {
      this.slideDirection = '';
    }, 300);
  }

  navigateWithOffset(offsetDays: number) {
    const currentDate = this.dateString() ? new Date(this.dateString()) : new Date();
    currentDate.setDate(currentDate.getDate() + offsetDays);

    this.generateWeekDays(currentDate);

    const currentWeekDays = this.weekDays();
    currentWeekDays.forEach(day => (day.active = isSameDay(day.dateObject, currentDate)));

    // ✅ Update signal to trigger change detection
    this.weekDays.set([...currentWeekDays]);
    this.changeDetectorRef.markForCheck();

    const formatted = [
      currentDate.getFullYear(),
      String(currentDate.getMonth() + 1).padStart(2, '0'),
      String(currentDate.getDate()).padStart(2, '0'),
    ].join('-');

    if (offsetDays > 0) {
      this.bodySlideDirection.set('slide-in-left');
    } else {
      this.bodySlideDirection.set('slide-in-right');
    }

    this.router.navigate(
      formatted === this.todayDateString ? ['/today'] : ['/', formatted]
    );

    setTimeout(() => {
      this.bodySlideDirection.set('');
    }, 300);
  }

  /**
   * ✅ Mark data flags for week days using signal
   * Uses the SAME logic as UI display (EntryService) to ensure consistency
   */
  private markWeekDataFlags(baseDate: Date) {
    const currentWeekDays = this.weekDays();

    // Get date strings for the week
    const dateStrings = currentWeekDays.map(day => {
      return [
        day.dateObject.getFullYear(),
        String(day.dateObject.getMonth() + 1).padStart(2, '0'),
        String(day.dateObject.getDate()).padStart(2, '0'),
      ].join('-');
    });

    // Get hasData for all dates using the same logic as UI display
    const hasDataMap = this.featureService.getHasDataForDates(dateStrings);

    // Update each day's hasData flag
    const updatedWeekDays = currentWeekDays.map((day, index) => ({
      ...day,
      hasData: hasDataMap[dateStrings[index]] || false
    }));

    this.weekDays.set([...updatedWeekDays]);
  }

  /**
   * ✅ Generate week days using signal
   */
  generateWeekDays(baseDate: Date = new Date()) {
    const start = startOfWeek(baseDate, { weekStartsOn: this.ss.effectiveWeekStartDay() as Day });
    const today = new Date();

    // Define a map from JS weekday numbers to your language keys
    const dayKeyMap: TranslationKey[] = [
      'common_daySunday',
      'common_dayMonday',
      'common_dayTuesday',
      'common_dayWednesday',
      'common_dayThursday',
      'common_dayFriday',
      'common_daySaturday'
    ];

    // Get date-fns locale for narrow day name formatting
    const langCode = this.ss.appLanguageCode() || 'en';
    const dateFnsLocale = DATE_FNS_LOCALE_MAP[langCode] || enUS;

    const newWeekDays = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(start, i);
      const dayKey = dayKeyMap[d.getDay()]; // e.g., "common_dayMonday"
      const fullName = this.ss.t(dayKey) || dayKey;
      // Use date-fns narrow format (EEEEE) for proper single-char day abbreviation
      // This handles Arabic correctly (ح, ن, ث, etc.) instead of fullName[0] which gives 'ا' for all days
      const narrowName = dateFnsFormat(d, 'EEEEE', { locale: dateFnsLocale });

      return {
        name: fullName,
        narrowName,
        date: d.getDate(),
        dateObject: d,
        active: isSameDay(d, baseDate),
        isToday: isSameDay(d, today),
        hasData: false
      };
    });

    this.weekDays.set(newWeekDays);
    this.markWeekDataFlags(baseDate);
    this.changeDetectorRef.markForCheck();
  }



  /**
   * ✅ Get week days as computed signal
   */
  getWeekDays(): any[] {
    return this.weekDays();
  }

  /**
   * ✅ TrackBy function for *ngFor
   */
  trackByDate(index: number, day: any): string {
    return `${day.dateObject.getFullYear()}-${day.dateObject.getMonth()}-${day.dateObject.getDate()}-${day.hasData}-${day.active}`;
  }

  // ========================================
  // Mobile Weekly Calendar Event Handlers
  // ========================================

  /**
   * Handle day selection from mobile weekly calendar
   * Delegates to existing selectDay method
   */
  onMobileDaySelected(day: WeekDay): void {
    this.selectDay(day);
  }

  /**
   * Handle week navigation from mobile weekly calendar swipe
   * 'next' = swipe left = +7 days (to future)
   * 'prev' = swipe right = -7 days (to past)
   */
  onMobileNavigateWeek(direction: 'next' | 'prev'): void {
    const offset = direction === 'next' ? 7 : -7;
    this.slideAndNavigate(offset);
  }

  /**
   * Handle swipe down to open month calendar from mobile weekly calendar
   * Opens the existing mat-datepicker
   */
  onMobileOpenMonthCalendar(): void {
    this.openCalendar();
  }

  openKebabMenu() {
    // Close the calendar picker if it's open
    if (this.picker && this.picker.opened) {
      this.picker.close();
    }

    const dialogRef = this.modalService.openLevel3(TodayViewComponent, {}, {
      disableClose: true
    });
    return this.modalService.getClosedObservable(dialogRef);
  }

  openGoal() {
    const confirmDialog = this.dialog.open(InputGoalComponent, {
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        value: this.viewSettings.userGoal() || ''
      },
    });
    return confirmDialog.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        // Update both ViewSettingsStore and SettingsService._viewSignals
        this.viewSettings.updateSetting('userGoal', dialogResult);
        this.ss.viewSignals()['userGoal'].set(dialogResult);
        this.ss.updateViewSettings();
      }
    });
  }

  openUnscheduledTodos() {
    this.modalService.openLevel2(UnscheduledTodosComponent, { filterType: 'incomplete' });
  }

  openInputCalendar() {
    const dialogRef = this.modalService.openModalLevel4(InputCalendarComponent, {
      data: new InputCalendarModel(this.currentDate)
    });

    this.modalService.getClosedObservable(dialogRef).subscribe((dialogResult: Date | null) => {
      if (dialogResult) {
        const dateString = getDateString(dialogResult);
        this.router.navigate(dateString === this.todayDateString ? ['/today'] : ['/', dateString]);
      }
    });
  }

  openTodo() {
    const dateString = getDateString();
    this.featureService.openTodoForm(null, dateString, true);
  }

  openList(isAlert: boolean = true) {
    this.featureService.openListForm(null, isAlert);
  }

  openNote(isAlert: boolean = true) {
    this.featureService.openNoteForm(null, isAlert);
  }

  openHabitTypeSelect() {
    this.featureService.openHabitTypeSelect();
  }

  openJournalSetup() {
    this.featureService.openJournalSetupForm(null, true);
  }

  openMoneyTrackerSetup() {
    this.featureService.openMoneyTrackerSetupForm(null, true);
  }

  openCalendarTypeSelect() {
    this.featureService.openCalendarTypeSelect();
  }

  openMoneyTracker(transactionType: 'income' | 'expense') {
    this.featureService.openMoneyTransactionForm(transactionType, null, true);
  }

  openHabitTimers() {
    this.isclockedToOpenHabitTimersDialog.set(true);
    const dialogRef = this.dialog.open(HabitTimerDialogComponent, {
      id: 'habitTimerListDialog',
      maxWidth: '350px',
      width: '100%',

    });

    dialogRef.afterClosed().subscribe((dialogResult: Date | null) => {
      if (dialogResult) {
        log.debug('openHabitTimers', 'Dialog result', { dialogResult });

      }
      this.isclockedToOpenHabitTimersDialog.set(false);
    });
  }

  focusLevelSelect() {
    this.featureService.openFocusSetupSelect();
  }

  habitSetupSelect() {
    this.featureService.openHabitSetupSelect();
  }

  journalSetupSelect() {
    this.featureService.openJournalSetupSelect();
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('today', previousScreen);
        this.storageService.setCurrentScreen('today');
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('today', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}